import { useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, ArrowRight, CheckCircle } from "lucide-react";
import { HIPAABadge } from "@/components/HIPAACompliance";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await API.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="forgot-password-sent">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-4"><HIPAABadge variant="pill" /></div>
          <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Check Your Email</h2>
            <p className="text-slate-500 text-sm mb-6">If an account exists for {email}, you&apos;ll receive a password reset link.</p>
            <Link to="/login" className="text-sm text-purple-600 hover:text-purple-700 font-medium">Back to Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-5 h-5 text-white" /></div>
          <span className="font-bold text-2xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
        </Link>
        <div className="flex justify-center mb-4"><HIPAABadge variant="pill" /></div>
        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
          <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Reset Password</h1>
          <p className="text-slate-500 text-sm mb-6">Enter your email and we&apos;ll send you a reset link.</p>
          {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm mb-4" data-testid="forgot-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1 rounded-xl" required data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-base" data-testid="forgot-submit-btn">
              {loading ? "Sending..." : "Send Reset Link"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
          <p className="text-sm text-slate-500 text-center mt-6">
            <Link to="/login" className="text-purple-600 hover:text-purple-700 font-medium">Back to Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
